/**
 * <s-help-side> — Help Center sidebar drawer for small screens.
 *
 * On desktop the sidebar panel is always shown in the page. Below lg the
 * "Browse docs" button (commandfor="help-side-panel" command="--toggle")
 * opens the panel as a drawer: sets [data-open] on the panel and the host,
 * locks page scroll, closes on Esc, on the backdrop or on a link click, and
 * scrolls the current guide into view.
 */
class SHelpSide extends HTMLElement {
  #ac;
  #panel;
  #trigger = null;

  connectedCallback() {
    this.#panel = this.querySelector('#help-side-panel');
    if (!this.#panel) return;
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.#panel.addEventListener('command', (e) => {
      if (e.command === '--toggle') {
        this.#trigger = e.source instanceof HTMLElement ? e.source : null;
        this.#toggle(!this.#panel.hasAttribute('data-open'));
      }
      if (e.command === '--close') this.#toggle(false);
    }, opts);

    this.addEventListener('click', (e) => {
      if (e.target === this.querySelector('[data-help-side-backdrop]')) this.#toggle(false);
      if (e.target.closest?.('a') && this.#panel.hasAttribute('data-open')) this.#toggle(false, false);
    }, opts);

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.#panel.hasAttribute('data-open')) this.#toggle(false);
    }, opts);

    window.matchMedia('(min-width: 64rem)').addEventListener('change', (e) => {
      if (e.matches) this.#toggle(false, false);
    }, opts);

    // Desktop: keep the current guide visible inside the scrolling tree.
    requestAnimationFrame(() => this.#revealCurrent());
  }

  disconnectedCallback() {
    this.#ac?.abort();
    document.documentElement.style.removeProperty('overflow');
  }

  #revealCurrent() {
    const current = this.querySelector('[data-help-current]');
    const scroller = this.querySelector('[data-help-scroll]');
    if (!current || !scroller || scroller.scrollHeight <= scroller.clientHeight) return;
    const top = current.getBoundingClientRect().top - scroller.getBoundingClientRect().top;
    scroller.scrollTop += top - scroller.clientHeight / 3;
  }

  #toggle(open, restoreFocus = true) {
    this.#panel.toggleAttribute('data-open', open);
    this.toggleAttribute('data-open', open);
    this.querySelectorAll('[commandfor="help-side-panel"][command="--toggle"]').forEach((b) => b.setAttribute('aria-expanded', String(open)));
    if (open) {
      document.documentElement.style.overflow = 'hidden';
      requestAnimationFrame(() => {
        this.#revealCurrent();
        this.#panel.querySelector('a, button')?.focus();
      });
    } else {
      document.documentElement.style.removeProperty('overflow');
      if (restoreFocus) this.#trigger?.focus();
    }
  }
}

customElements.define('s-help-side', SHelpSide);
